import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  acceptInvitation,
  createWorkspace,
  deleteInvitation,
  deleteWorkspace,
  getWorkspace,
  inviteMember,
  listMembers,
  listMyInvitations,
  listSentInvitations,
  listWorkspaces,
  removeMember,
  renameWorkspace,
} from "@/lib/api/workspaces";
import { countFinalisedInvoices } from "@/lib/api/account";
import { useAuth } from "@/lib/auth/auth-context";
import type { InvitationsResponse } from "@/lib/core/pb-types";
import { useCurrentWorkspace } from "@/lib/workspace/workspace-context";

export const workspaceKeys = {
  all: ["workspace"] as const,
  list: (memberships: string) => [...workspaceKeys.all, "list", memberships] as const,
  current: (id: string) => [...workspaceKeys.all, "current", id] as const,
  members: (id: string) => [...workspaceKeys.all, "members", id] as const,
  sentInvitations: (id: string) => [...workspaceKeys.all, "invitations", id] as const,
  myInvitations: ["invitations", "mine"] as const,
};

/** All workspaces the user belongs to (refetched when the membership on the auth record changes). */
export function useWorkspaces() {
  const { user } = useAuth();
  const memberships = ((user?.workspaces as string[] | undefined) ?? []).join(",");
  return useQuery({
    queryKey: workspaceKeys.list(memberships),
    queryFn: listWorkspaces,
    enabled: Boolean(user),
  });
}

export function useWorkspace() {
  const { workspaceId } = useCurrentWorkspace();
  return useQuery({
    queryKey: workspaceKeys.current(workspaceId ?? ""),
    queryFn: getWorkspace,
    enabled: Boolean(workspaceId),
  });
}

function useInvalidateWorkspaces() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
}

export function useRenameWorkspace() {
  const invalidate = useInvalidateWorkspaces();
  return useMutation({ mutationFn: renameWorkspace, onSuccess: invalidate });
}

export function useCreateWorkspace() {
  const { switchWorkspace } = useCurrentWorkspace();
  return useMutation({
    mutationFn: createWorkspace,
    onSuccess: (workspace) => switchWorkspace(workspace.id),
  });
}

export function useDeleteWorkspace() {
  return useMutation({ mutationFn: deleteWorkspace });
}

export function useMembers() {
  const { workspaceId } = useCurrentWorkspace();
  return useQuery({
    queryKey: workspaceKeys.members(workspaceId ?? ""),
    queryFn: listMembers,
    enabled: Boolean(workspaceId),
  });
}

export function useRemoveMember() {
  const invalidate = useInvalidateWorkspaces();
  return useMutation({ mutationFn: removeMember, onSuccess: invalidate });
}

export function useSentInvitations(enabled: boolean) {
  const { workspaceId } = useCurrentWorkspace();
  return useQuery({
    queryKey: workspaceKeys.sentInvitations(workspaceId ?? ""),
    queryFn: listSentInvitations,
    enabled: enabled && Boolean(workspaceId),
  });
}

export function useInviteMember() {
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspace();
  return useMutation({
    mutationFn: inviteMember,
    onSuccess: async (invitation) => {
      // A list request that started before the invitation existed must not overwrite it.
      const key = workspaceKeys.sentInvitations(workspaceId ?? "");
      await queryClient.cancelQueries({ queryKey: key });
      queryClient.setQueryData<InvitationsResponse[]>(key, (current = []) => [
        ...current.filter((item) => item.id !== invitation.id),
        invitation,
      ]);
      await queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
    },
  });
}

export function useMyInvitations() {
  const { user } = useAuth();
  return useQuery({
    queryKey: workspaceKeys.myInvitations,
    queryFn: listMyInvitations,
    enabled: Boolean(user),
  });
}

export function useDeleteInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteInvitation,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: workspaceKeys.all }),
        queryClient.invalidateQueries({ queryKey: workspaceKeys.myInvitations }),
      ]),
  });
}

export function useAcceptInvitation() {
  const { switchWorkspace } = useCurrentWorkspace();
  return useMutation({
    mutationFn: acceptInvitation,
    onSuccess: (workspace) => switchWorkspace(workspace.id),
  });
}

/**
 * Finalised invoices a deletion would remove (current workspace, or all owned ones), read when
 * the deletion dialog opens.
 */
export function useFinalisedInvoiceCount(scope: "workspace" | "owned", enabled: boolean) {
  return useQuery({
    queryKey: ["finalised-count", scope],
    queryFn: () => countFinalisedInvoices(scope),
    enabled,
  });
}
